import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const http = axios.create({ baseURL: API, withCredentials: true });

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const formatApiError = (detail, fallback = "Something went wrong. Please try again.") => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return fallback;
};

export const useAdminSession = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    http.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await http.post("/auth/login", { email, password });
    setUser(data.user);
  };

  const logout = useCallback(async () => {
    try { await http.post("/auth/logout"); } catch { /* cookie may already be gone */ }
    setUser(null);
  }, []);

  return { user, checking, login, logout };
};

export const useLeads = (onUnauthorized) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const guard = useCallback((err) => { if (err.response?.status === 401) onUnauthorized(); }, [onUnauthorized]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/leads");
      setLeads(data);
    } catch (err) {
      guard(err);
    } finally {
      setLoading(false);
    }
  }, [guard]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await http.patch(`/admin/leads/${id}/status`, { status });
    } catch (err) {
      setLeads(prev);
      guard(err);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await http.get("/admin/leads/export.csv", { responseType: "blob" });
      downloadBlob(res.data, `auxieron-leads-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      guard(err);
    } finally {
      setExporting(false);
    }
  };

  const replaceLead = (upd) => setLeads((ls) => ls.map((l) => (l.id === upd.id ? upd : l)));

  return { leads, loading, exporting, load, setStatus, exportCsv, replaceLead };
};
